import React from 'react';
import { generateDiff, getChangedFields, formatValue, formatFieldName } from '../../../utils/diff';
import styles from './ReviewChangesDialog.module.css';

interface ReviewChangesDialogProps {
  isOpen: boolean;
  title: string;
  originalData: Record<string, any> | null;
  modifiedData: Record<string, any>;
  onConfirm: () => void;
  onCancel: () => void;
  entityType?: string;
}

const ReviewChangesDialog: React.FC<ReviewChangesDialogProps> = ({
  isOpen,
  title,
  originalData,
  modifiedData,
  onConfirm,
  onCancel,
  entityType = 'entity'
}) => {
  if (!isOpen) return null;

  // Generate diff between original and modified data
  const diffs = originalData
    ? generateDiff(originalData, modifiedData)
    : Object.keys(modifiedData)
      .filter(key => {
        const value = modifiedData[key];
        // Filter out truly empty values for create operations
        // Keep: false, 0, and other falsy-but-valid values
        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      })
      .map(key => ({
        field: key,
        oldValue: null,
        newValue: modifiedData[key],
        hasChanged: true
      }));

  const changedFields = getChangedFields(diffs);

  // If creating new entity, show all fields with values
  const isCreate = !originalData;

  return (
    <div className={styles['review-overlay']} onClick={onCancel}>
      <div className={styles['review-dialog']} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className={styles['review-header']}>
          <h2>{title}</h2>
          <p className={styles['review-subtitle']}>
            {isCreate
              ? `Review the new ${entityType} details before saving to Production.`
              : `Review the changes before applying them to Production.`}
          </p>
        </div>

        <div className={styles['review-body']}>
          {changedFields.length === 0 ? (
            <div className={styles['no-changes']}>
              <p>No changes detected.</p>
            </div>
          ) : (
            <div className={styles['changes-list']}>
              {changedFields.map((diff, index) => (
                <div key={index} className={styles['change-item']}>
                  <div className={styles['field-name']}>{formatFieldName(diff.field)}</div>
                  <div className={styles['field-comparison']}>
                    <div className={`${styles['field-value']} ${styles['old-value']}`}>
                      <span className={styles['value-label']}>Before:</span>
                      <div className={styles['value-content']}>{formatValue(diff.oldValue)}</div>
                    </div>
                    <div className={styles['field-arrow']}>→</div>
                    <div className={`${styles['field-value']} ${styles['new-value']}`}>
                      <span className={styles['value-label']}>After:</span>
                      <div className={styles['value-content']}>{formatValue(diff.newValue)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={styles['production-warning']}>
            <svg
              className={styles['warning-icon']}
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className={styles['warning-text']}>
              <strong>Production Database</strong>
              <p>
                These changes will be applied to the production database immediately.
                This action cannot be undone automatically.
              </p>
            </div>
          </div>
        </div>

        <div className={styles['review-footer']}>
          <button onClick={onCancel} className={styles['btn-cancel']}>
            Cancel
          </button>
          <button onClick={onConfirm} className={styles['btn-confirm-production']}>
            Confirm Production {isCreate ? 'Create' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewChangesDialog;
